import Vue from 'vue';
import jwt_decode from "jwt-decode";

export default {
  namespaced: true,
  state: {
    loggedIn: {},
    passwordResetEmailSent: false,
    successTokenValidation: null,
    autofilledUserName: '',
    successSendUserName: null,
    // Silently login setup
    silentlyLoginTimeout: null,
    secondsBeforeSilentlyLogin: 150,
    killSilentlyAfterTimes: 3,
  },
  getters: {
    isLoggedIn(state) {
      return state.loggedIn.token || false;
    },
  },
  mutations: {
    async logOut() {
      try {
        await this.$axios.post('/logout', {});
      } catch (err) {
        // Silence is golden!
      }

      // Log the user out no matter what.
      this.$sCache.clear('loggedIn');
      document.location = '/';
    },
    setToken(state) {
      let loggedIn = {};
      if (typeof state.loggedIn.token === 'string') {
        return;
      }

      try {
        loggedIn = this.$sCache.get('loggedIn');
        if (loggedIn) {
          state.loggedIn = loggedIn;
          state.isLoggedIn = true;
        }
      } catch (err) {
        console.warn(err);

        // Silence is power...
        state.loggedIn = {};
      }
    },
    updateAuth(state, payload) {
      const decodedToken = jwt_decode(payload.token);
      if (process.env.VUE_APP_DEBUG) {
        console.log("Decoded token", decodedToken);
      }

      const expiresAt = this.$dayjs.unix(decodedToken.exp);
      if (process.env.VUE_APP_DEBUG) {
        console.log("expiresAt", decodedToken.exp);
      }

      payload.expirationTime = expiresAt.format();
      if (process.env.VUE_APP_DEBUG) {
        console.log("payload.expirationTime", payload.expirationTime);
      }

      let tempAuth = {};
      Object.assign(tempAuth, state.loggedIn, payload);

      if (process.env.VUE_APP_DEBUG) {
        console.log("Assigned temp authentication", tempAuth);
      }

      Vue.set(state, 'loggedIn', tempAuth);
      state.isLoggedIn = true;

      if (process.env.VUE_APP_DEBUG) {
        console.log("expiresAt.toDate", expiresAt.toDate());
      }

      this.$sCache.set('loggedIn', state.loggedIn, expiresAt.toDate());
    },

    /**
     * FORGOT PASSWORD MUTATIONS
     */
    passwordResetEmailSent(state) {
      state.passwordResetEmailSent = true;
    },
    setSuccessTokenValidation(state, value) {
      state.successTokenValidation = value;
    },
    setAutofilledUsername(state, username) {
      state.autofilledUserName = username;
    },
    setSilentlyLoginTimeout(state, timeout) {
      state.silentlyLoginTimeout = timeout;
    },
    updateKillSilentlyAfterTimes(state) {
      state.killSilentlyAfterTimes--;
    },

    /**
     * FORGOT USERNAME MUTATIONS
     */
    setSuccessSendUserName(state, value) {
      state.successSendUserName = value;
    },
  },
  actions: {
    login ({ dispatch, commit }, payload) {

      const doNotRedirect = payload.doNotRedirect || false;
      if (doNotRedirect) {
        payload.doNotRedirect = null;
      }

      try {
        commit('updateAuth', payload);
        if (doNotRedirect) {
          dispatch('silentlyLoginTimeout');
        } else {
          if (payload.roles.includes('SysAdmin') || payload.roles.includes('DeptHead')) {
            document.location = '/a/filer-administration';
          } else {
            document.location = '/u/file-now';
          }
        }
      } catch (err) {
        this.$warning(`Oops! Something happened when logging you in, please try again, if the problem persist contact the system administrator`);
        return Promise.reject(err);
      }
    },
    startSession({ dispatch }, user) {
      return this.$axios.post('/authenticate', user)
        .then(async (response) => {
          await dispatch('login', {
            ...response.data,
          });
          return true;
        })
        .catch((error) => {
          console.log(error);
          this.$error(`Unable to login, please verify your username and password`);
          return false;
        });
    },
    async confirmAccountToken({ dispatch, rootState }, data) {

      let filerId = await dispatch('strToNum',
        {
          items: null,
          keys: data.filerId,
        }, { root: true });

      return new Promise((resolve, reject) => {
        this.$axios({
          url: rootState.endpoints.accountConfirmation,
          method: 'post',
          data: {
            filerId,
            emailToken: data.emailToken,
          },
        }).then((response) => {
          console.log(response);
          resolve();
        }).catch(err => {
          console.log(err);
          reject();
        });
      });
    },
    async confirmPasswordResetToken({ dispatch, rootState }, data) {

      let filerId = await dispatch('strToNum', {
        items: null,
        keys: data.filerId,
      }, { root: true });

      let response = await this.$axios({
        url: rootState.endpoints.validateToken,
        method: 'post',
        data: {
          filerId: filerId,
          emailToken: data.emailToken,
        },
      });

      if (response) {
        return true;
      }

      return false;

    },
    validateToken({ commit }, params) {
      this.$axios.get('/validate-token', params)
        .then(response => {
          if (response) {
            commit('setSuccessTokenValidation', true);
          }
        })
        .catch(err => {
          if (process.env.VUE_APP_DEBUG) {
            console.warn(err);
          }
          this.$warning(`Your reset password access token is invalid, please refresh the page and try again`);
        });
    },
    resendConfirmationEmail({ rootState }, emailAddress) {
      return new Promise((resolve, reject) => {
        this.$axios({
          url: rootState.endpoints.resendConfirmationEmail,
          method: 'post',
          data: {
            emailAddress,
          },
        }).then((response) => {
          console.log(response);
          if (response.status === 204) {
            resolve();
          }
        }).catch(error => {
          reject(error);
        });
      });
    },
    resetPassword({ commit }, params) {
      this.$axios.get('/reset-password', params)
        .then(() => {
          this.$success('Your password has been successfully changed');
          commit('setAutofilledUsername', params.email);
          setTimeout(() => {
            this.$router.push('/login');
          }, 250);
        })
        .catch(err => {
          if (process.env.VUE_APP_DEBUG) {
            console.warn(err);
          }
          this.$warning(`There was an issue resetting the password, please refresh and try again`);
        });
    },
    /**
     * Forgot Username API Requests
     */
    sendUserName({
      commit,
    }, params) {
      this.$axios.get('/forgot-username', params)
        .then((response) => {
          commit('setSuccessSendUserName', response.data);
        })
        .catch(err => {
          if (process.env.VUE_APP_DEBUG) {
            console.warn(err);
          }
          this.$warning(`There was an issue sending the username to the email,
            please refresh the page and try again`);
        });
    },
    async maintenanceMode({ rootState }) {
      let isMaintenanceActive;
      let status = false;

      //First check if maintenance mode has been manually set
      try {
        isMaintenanceActive = await this.$axios.get(`${rootState.endpoints.controlValue}`, {
          params: {
            code: 'MAINTENANCEMODE',
          },
        });

        console.log(isMaintenanceActive);

        if (isMaintenanceActive.data[0].active === 'Y' && isMaintenanceActive.data[0] && isMaintenanceActive.data[0].value == 1) {
          status = true;
        }

      } catch {
        status = false;
      }

      //Check if maintenace mode has been set by the users import
      if (!status) {
        try {
          let isImportMode = await this.$axios.get('admin/users/status-check');
          if (isImportMode.data === true) {
            status = true;
          }
        } catch {
          status = false;
        }
      }

      return status;

    },
    /**
     * Execute Login Silently
     */
    async runLoginSilently({ state, commit, dispatch }) {
      console.log("Loging in silently");
      clearTimeout(state.silentlyLoginTimeout);

      let loggedInfo = await this.$sCache.get('loggedIn');

      return new Promise((resolve, reject) => {
        this.$axios.post('/refresh-token', { refreshToken: loggedInfo.token })
          .then((response) => {
            const newAuth = response.data;
            newAuth.doNotRedirect = true;

            dispatch('login', newAuth)
              .then(() => {
                resolve('It looks like everything goes good!');
              });
          })
          .catch((err) => {

            console.log("Ops! something when wrong doing the silently login... trying again.");
            console.warn(err);

            if (state.killSilentlyAfterTimes > 0) {
              dispatch('silentlyLoginTimeout');
              commit('updateKillSilentlyAfterTimes');
            }
            reject(err);
          });
      });
    },

    /**
     * Set Silently Login Timeout
     */
    silentlyLoginTimeout({ state, commit, dispatch }) {
      if (typeof state.loggedIn.expirationTime !== 'undefined') {
        try {

          const expire_time = this.$dayjs(state.loggedIn.expirationTime);

          const diff = expire_time.diff(this.$dayjs(), 'seconds');

          if (diff > state.secondsBeforeSilentlyLogin) {
            if (state.silentlyLoginTimeout) {
              clearTimeout(state.silentlyLoginTimeout);
            }
            const tempTimeout = setTimeout(() => {
              dispatch('runLoginSilently');
            }, ((diff - state.secondsBeforeSilentlyLogin) * 1000));
            commit('setSilentlyLoginTimeout', tempTimeout);
          } else {
            dispatch('runLoginSilently');
          }
        } catch (err) {
          console.warn(err);
        }
      }
    },
  },
};
